/* Ash Mega Menu — hover-open with grace, single-active across page,
 * click still works for touch / keyboard. */
class AshMegaMenu extends HTMLElement {
  connectedCallback() {
    this._trigger = this.querySelector('[data-ash-mm-trigger]');
    this._panel = this.querySelector('[data-ash-mm-panel]');
    this._rail = this.querySelector('[data-ash-mm-rail]');
    if (!this._trigger || !this._panel) return;

    /* For the block-based architecture: each rendered _ash-mega-tab
     * pane carries its label/URL/id as data attributes. The rail is
     * empty in liquid; we build the rail buttons from those panes here
     * so the user only manages tabs as blocks, not as duplicate rail
     * entries. Safe no-op for legacy panels that ship their own rail. */
    if (this._rail && this._rail.children.length === 0) {
      var panes = this._panel.querySelectorAll('[data-ash-mn-tab-id]');
      var i = 0;
      var firstId = null;
      panes.forEach(function (pane) {
        var tid = pane.getAttribute('data-ash-mn-tab-id');
        var tlabel = pane.getAttribute('data-ash-mn-tab-label') || ('Tab ' + (++i));
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ash-mn__rail-btn button-unstyled';
        btn.setAttribute('data-ash-mm-tab', tid);
        btn.setAttribute('role', 'tab');
        btn.textContent = tlabel;
        if (firstId === null) {
          firstId = tid;
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          pane.classList.add('is-active');
          pane.removeAttribute('hidden');
        } else {
          btn.setAttribute('aria-selected', 'false');
          pane.setAttribute('hidden', '');
        }
        var li = document.createElement('li');
        li.className = 'ash-mn__rail-item';
        li.appendChild(btn);
        this._rail.appendChild(li);
        pane.setAttribute('data-ash-mm-pane', tid);
      }, this);
    }

    var self = this;
    this._closeTimer = null;

    /* Hover open with grace period so moving cursor trigger → panel
       doesn't close the panel. */
    this.addEventListener('mouseenter', function () {
      clearTimeout(self._closeTimer);
      self.open();
    });
    this.addEventListener('mouseleave', function () {
      clearTimeout(self._closeTimer);
      self._closeTimer = setTimeout(function () { self.close(); }, 160);
    });

    /* Click still toggles — useful for keyboard / touch. */
    this._trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggle();
    });

    /* Tab switching — click + hover */
    if (this._rail) {
      this._rail.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ash-mm-tab]');
        if (!btn) return;
        /* L2 collection links: <a href> navigates; buttons still switch tabs only. */
        if (btn.tagName === 'A' && btn.getAttribute('href') && btn.getAttribute('href') !== '#') {
          return;
        }
        e.preventDefault();
        self.activate(btn.getAttribute('data-ash-mm-tab'));
      });
      this._rail.addEventListener('mouseover', function (e) {
        var btn = e.target.closest('[data-ash-mm-tab]');
        if (!btn) return;
        self.activate(btn.getAttribute('data-ash-mm-tab'));
      });
    }

    /* Sub-rail (L3) switching inside an L2 pane — click + hover */
    this._panel.addEventListener('click', function (e) {
      var sbtn = e.target.closest('[data-ash-mm-subtab]');
      if (!sbtn) return;
      e.preventDefault();
      self.activateSub(sbtn);
    });
    this._panel.addEventListener('mouseover', function (e) {
      var sbtn = e.target.closest('[data-ash-mm-subtab]');
      if (!sbtn) return;
      self.activateSub(sbtn);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });
    document.addEventListener('click', function (e) {
      if (!self.isOpen()) return;
      if (self.contains(e.target)) return;
      self.close();
    });
    document.addEventListener('ash-mm:open', function (e) {
      if (e.detail && e.detail.source !== self) self.close();
    });

    /* Close on any meaningful page scroll. The check ignores the tiny
       scroll deltas the layout itself fires when the panel mounts. */
    var lastY = window.scrollY;
    window.addEventListener('scroll', function () {
      if (!self.isOpen()) { lastY = window.scrollY; return; }
      if (Math.abs(window.scrollY - lastY) > 4) self.close();
      lastY = window.scrollY;
    }, { passive: true });
  }

  isOpen() { return this.hasAttribute('data-open'); }
  toggle() { this.isOpen() ? this.close() : this.open(); }

  open() {
    if (this.isOpen()) return;
    this.setAttribute('data-open', '');
    this._trigger.setAttribute('aria-expanded', 'true');
    document.dispatchEvent(new CustomEvent('ash-mm:open', { detail: { source: this } }));

    var active = this.querySelector('[data-ash-mm-tab].is-active');
    if (!active) {
      var first = this.querySelector('[data-ash-mm-tab]');
      if (first) this.activate(first.getAttribute('data-ash-mm-tab'));
    }
  }
  close() {
    this.removeAttribute('data-open');
    this._trigger.setAttribute('aria-expanded', 'false');
  }
  activate(id) {
    if (!id) return;
    this.querySelectorAll('[data-ash-mm-tab]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-ash-mm-tab') === id);
      b.setAttribute('aria-selected', b.getAttribute('data-ash-mm-tab') === id ? 'true' : 'false');
    });
    this.querySelectorAll('[data-ash-mm-pane]').forEach(function (p) {
      var match = p.getAttribute('data-ash-mm-pane') === id;
      p.classList.toggle('is-active', match);
      if (match) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
    /* Mobile cards position auto-resolve: when the trigger setting
       is "auto", a pane's card count decides whether cards sit
       above (3+ cards) or below (≤2 cards) the rail. */
    if (this._panel && this._panel.getAttribute('data-ash-mn-mobile-cards') === 'auto') {
      var active = this.querySelector('[data-ash-mm-pane].is-active');
      var count = active ? active.querySelectorAll('.ash-mn__card').length : 0;
      this._panel.setAttribute('data-mobile-cards-resolved', count >= 3 ? 'top' : 'bottom');
    }
  }
  activateSub(btn) {
    var id = btn.getAttribute('data-ash-mm-subtab');
    if (!id) return;
    var parentPane = btn.closest('[data-ash-mm-pane]');
    var scope = parentPane || this;
    scope.querySelectorAll('[data-ash-mm-subtab]').forEach(function (b) {
      if (b.closest('[data-ash-mm-pane]') !== parentPane) return;
      var match = b.getAttribute('data-ash-mm-subtab') === id;
      b.classList.toggle('is-active', match);
      b.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    scope.querySelectorAll('[data-ash-mm-subpane]').forEach(function (p) {
      if (p.closest('[data-ash-mm-pane]') !== parentPane) return;
      var match = p.getAttribute('data-ash-mm-subpane') === id;
      p.classList.toggle('is-active', match);
      if (match) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }
}

if (!customElements.get('ash-mega-menu')) {
  customElements.define('ash-mega-menu', AshMegaMenu);
}
